import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useConstants } from "utils/hooks";
import type { OptionType } from "components/inputs";
import { Select, Checkbox } from "components/inputs";

import Button from "components/Button/Button";
import {
  useSendTinFileMutation,
  useSendClaimFileMutation,
  useSendQueryFileMutation,
} from "services/rre-api";
import "./SendCMSFileModal.scss";
import KaboodleToaster from "components/common/KaboodleToaster";
import KaboodleModal from "./KaboodleModal";
import Card from "components/Card/Card";

interface SendCMSFileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type FormData = {
  file_type: OptionType;
  exclude_tin: boolean;
};

const SendCMSFileModal = ({ isOpen, onClose }: SendCMSFileModalProps) => {
  const { rreId } = useParams<{ rreId?: string }>();
  const { constants } = useConstants();

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      exclude_tin: false,
      file_type: null,
    },
  });

  const [sendTinFile] = useSendTinFileMutation();
  const [sendClaimFile] = useSendClaimFileMutation();
  const [sendQueryFile] = useSendQueryFileMutation();
  const fileType = watch("file_type")?.value;

  const onSubmitTinFile: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await sendTinFile({
          id: rreId,
          data: data,
        }).unwrap();

        if ((result as { id: number }).id) {
          reset();
          KaboodleToaster.custom(
            `File successfully submitted and can be tracked with id ${(result as { id: number }).id}`,
          );
          onClose();
        } else {
          KaboodleToaster.custom("An unknown error occurred.");
        }
      } catch (err) {
        console.error("Failed to send TIN File:", err);
        KaboodleToaster.error(err);
      }
    },
    [sendTinFile, rreId, reset, onClose],
  );

  const onSubmitClaimFile: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await sendClaimFile({
          id: rreId,
          data: data,
        }).unwrap();

        if ((result as { id: number }).id) {
          reset();
          KaboodleToaster.custom(
            `File successfully submitted and can be tracked with id ${(result as { id: number }).id}`,
          );
          onClose();
        } else {
          KaboodleToaster.custom("An unknown error occurred.");
        }
      } catch (err) {
        console.error("Failed to send Claim File:", err);
        KaboodleToaster.error(err);
      }
    },
    [sendClaimFile, rreId, reset, onClose],
  );

  const onSubmitQueryFile: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await sendQueryFile({
          id: rreId,
          data: data,
        }).unwrap();

        if ((result as { id: number }).id) {
          reset();
          KaboodleToaster.custom(
            `File successfully submitted and can be tracked with id ${(result as { id: number }).id}`,
          );
          onClose();
        } else {
          KaboodleToaster.custom("An unknown error occurred.");
        }
      } catch (err) {
        console.error("Failed to send Query File:", err);
        KaboodleToaster.error(err);
      }
    },
    [sendQueryFile, rreId, reset, onClose],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    let submitButton = null;
    switch (fileType) {
      case "T":
        submitButton = (
          <Button onClick={handleSubmit(onSubmitTinFile)}>Submit</Button>
        );
        break;
      case "C":
        submitButton = (
          <Button onClick={handleSubmit(onSubmitClaimFile)}>Submit</Button>
        );
        break;
      case "Q":
        submitButton = (
          <Button onClick={handleSubmit(onSubmitQueryFile)}>Submit</Button>
        );
        break;
      default:
        submitButton = <Button onClick={() => {}}>Submit</Button>;
    }

    return (
      <div className="flex jc-c ai-c gap-3 mt-1">
        {submitButton}
        <Button onClick={handleClose}>Close</Button>
      </div>
    );
  }, [
    handleSubmit,
    handleClose,
    fileType,
    constants,
    rreId,
    sendQueryFile,
    sendTinFile,
    sendClaimFile,
  ]);

  const cmsFileTypeOptions = constants?.CMS_FILE_TYPES?.filter(
    (option: OptionType) => {
      return option.value !== "U";
    },
  );

  return (
    <KaboodleModal isOpen={isOpen} onClose={onClose} footer={footerContent}>
      <Card heading="Manually Send CMS File">
        <Controller
          control={control}
          name="file_type"
          rules={{ required: "File type is required" }}
          render={({ field: { name, onChange } }) => (
            <Select
              name={name}
              placeholder="Select File Type"
              label="File Type"
              options={cmsFileTypeOptions}
              errors={errors}
              onChange={onChange}
            />
          )}
        />
        {fileType === "C" ? (
          <Controller
            control={control}
            name="exclude_tin"
            render={({ field: { name, onChange } }) => (
              <Checkbox
                key={name}
                name={name}
                label="Exclude sending TIN with Claim file"
                required={false}
                onChange={onChange}
              />
            )}
          />
        ) : null}
      </Card>
      {/* <div className="modal-content">
        <div className="data-block no-hover">
          <div className="delete-confirmation-text">
            <p>
              If you would like to send a test file you must select "Send Test
              File" under the CMS Communication tab or else a production file
              will be sent.
            </p>
          </div>
        </div>
      </div> */}
    </KaboodleModal>
  );
};

export default SendCMSFileModal;
